export type RequestMethod = 'post' | 'put' | 'patch' | 'get' | 'delete'

type ApiResponse = {
  status: number
  body: string
  json: unknown
  response: Response
  ok: boolean
}

const parseBody = async (
  response: Response
): Promise<{text: string; json?: unknown}> => {
  // don't assume the server will respect Content-Type
  const text = await response.text()
  try {
    return {
      text,
      json: JSON.parse(text)
    }
  } catch (e) {
    console.debug('Non JSON body')
  }
  return {
    text
  }
}

export default function api(
  base: string,
  getToken?: () => Promise<string | undefined> | string | undefined,
  refreshToken?: () => Promise<void> | undefined,
  defaultHeaders?: Record<string, string | undefined>
): (
  method: RequestMethod,
  route: string,
  data?: unknown,
  headers?: Record<string, string>
) => Promise<ApiResponse> {
  let refreshPromise: Promise<ApiResponse> | undefined
  const request = async (
    method: RequestMethod,
    route: string,
    data?: unknown,
    headers = {}
  ): Promise<ApiResponse> => {
    const token = getToken ? await getToken() : undefined
    let url = route.match(/^http/) ? route : `${base}${route}`

    if (method === 'get' && data) {
      // @ts-ignore URLSearchParams wants Record<string,string> but Record<string,unknown> will work fine
      const params = new URLSearchParams(data).toString()
      url = `${url}?${params}`
    }

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(defaultHeaders ? defaultHeaders : {}),
        ...headers,
        authorization: token ? `Bearer ${token}` : ''
      },
      ...(data && method !== 'get' ? {body: JSON.stringify(data)} : {})
    })
    const {status} = response

    if (status === 401) {
      if (refreshPromise) {
        return refreshPromise.then(() => request(method, route, data, headers))
      } else if (refreshToken) {
        refreshPromise = Promise.resolve(refreshToken())
          .then(() => request(method, route, data, headers))
          .catch(async () => {
            const {text, json} = await parseBody(response)
            return {status, body: text, json, response, ok: false}
          })
          .finally(() => {
            refreshPromise = undefined
          })
        return refreshPromise
      }
    }

    const {text, json} = await parseBody(response)
    return {status, body: text, json, response, ok: response.ok}
  }

  return request
}

export type ApiInstance = ReturnType<typeof api>
